import { cn } from "cn";

import { useActiveFilterToggle } from "../_hooks/use-active-filter-toggle";
import { useFilters } from "../_hooks/use-filters";
import { computeActiveKeys } from "../compute-active-keys";
import type { FilterFieldsConfig } from "../types";
import { ActiveFilter } from "./active-filter";
import { ActiveFilterClearAll } from "./active-filter-clear-all";
import { FilterMenu } from "./filter-menu";

type ActiveFilterBarProps = {
  fields: FilterFieldsConfig;
  className?: string;
};

/**
 * Generic active-filter bar. Reads typed `filters` + `filtersOrder`, renders one
 * `<ActiveFilter>` per active key (ordered) plus "Clear all". Replaces every
 * per-feature `active-filter-bar.tsx` switch.
 */
export const ActiveFilterBar = ({
  fields,
  className,
}: ActiveFilterBarProps) => {
  const { open } = useActiveFilterToggle();
  const { filters, filtersOrder } = useFilters();

  const fieldByKey = new Map(fields.map((field) => [field.key, field]));
  const activeKeys = computeActiveKeys(
    filters as Record<string, unknown>,
    filtersOrder,
  ).filter((key) => fieldByKey.has(key));

  if (!open || activeKeys.length === 0) return null;

  return (
    <div className={cn("py-2 px-6 border-t border-border-dark-low", className)}>
      <div
        className={cn(
          /* sorted */
          "flex",
          "flex-wrap gap-2",
        )}
      >
        {activeKeys.map((key) => {
          const field = fieldByKey.get(key);
          return field ? <ActiveFilter key={key} field={field} /> : null;
        })}
        {/* Inline "+ Add filter" — add more filters right where the chips are
            (mirrors office orders). Returns null once every field is active. */}
        <FilterMenu
          fields={fields}
          size="icon-xs"
          align="start"
          className="rounded-lg"
        />
        <ActiveFilterClearAll />
      </div>
    </div>
  );
};
